import React, { useEffect, useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../Context/AuthContext';
import { api } from '../../api';

const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleGoogleCallback = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Extract the authorization code from the URL
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const errorParam = urlParams.get('error');
        
        if (errorParam) {
          throw new Error(`Google authentication error: ${errorParam}`);
        }
        
        if (!code) {
          throw new Error('Authorization code not found in URL');
        }

        // Exchange the code for tokens
        const response = await api.post('/auth/google-auth', { 
          code 
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.data || !response.data.token || !response.data.user) {
          throw new Error('Invalid response from server');
        }

        const { token, user } = response.data;

        // Call the login function from AuthContext
        await login(token, user);

        // Redirect to dashboard or home page
        navigate('/Socialmedia/dashboard', { replace: true });
      } catch (error) {
        console.error('Google authentication failed:', error);
        setError(error.message || 'Google login failed');
        navigate('/login', { 
          state: { 
            error: error.message || 'Google login failed',
            from: location 
          },
          replace: true 
        });
      } finally {
        setLoading(false);
      }
    };

    handleGoogleCallback();
  }, [navigate, login, location]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FD5F20] mx-auto"></div>
          <p className="mt-4 text-gray-600">Signing in with Google...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-6 max-w-md mx-auto bg-white rounded-lg shadow-md">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Login Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-[#FD5F20] text-white rounded-md hover:bg-[#e0551d] transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default GoogleAuthCallback;